import { useEffect, useMemo, useState } from "react";
import { Laptop, Moon, Sun } from "lucide-react";

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

type ThemePreference = "auto" | "light" | "dark";

/**
 * Reads the persisted theme preference from localStorage, falling back to
 * "auto" when no value has been stored yet.
 */
function getStoredPreference(): ThemePreference {
  if (typeof window === "undefined") return "auto";

  const raw = localStorage.getItem("theme");
  return raw === "light" || raw === "dark" || raw === "auto" ? raw : "auto";
}

/**
 * Returns the current system-level preference.
 */
function getSystemPreference(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Given the user’s explicit preference (or "auto"), determine which theme is
 * actively applied right now.
 */
function resolveActiveTheme(pref: ThemePreference): "light" | "dark" {
  return pref === "auto" ? getSystemPreference() : pref;
}

// ------------------------------------------------------------------
// React component
// ------------------------------------------------------------------

export default function ThemeToggle() {
  const [preference, setPreference] = useState<ThemePreference>(() => getStoredPreference());

  // Whenever the preference or the system settings change, figure out what we
  // actually need to apply to the document root.
  const activeTheme = useMemo(() => resolveActiveTheme(preference), [preference]);

  // Keep the DOM in sync ----------------------------------------------------
  useEffect(() => {
    const root = document.documentElement;

    // Tailwind – relies on the `.dark` class.
    root.classList.toggle("dark", activeTheme === "dark");

    // daisyUI – relies on `data-theme="light|dark"`.
    root.setAttribute("data-theme", activeTheme);

    // Persist the preference so the early-boot script can apply it before the
    // React bundle loads.
    localStorage.setItem("theme", preference);
  }, [activeTheme, preference]);

  // Update theme automatically when the user changes their OS preference ----
  useEffect(() => {
    if (preference !== "auto" || typeof window === "undefined") return;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => {
      const root = document.documentElement;
      const systemTheme = mql.matches ? "dark" : "light";
      root.classList.toggle("dark", systemTheme === "dark");
      root.setAttribute("data-theme", systemTheme);
    };

    mql.addEventListener("change", listener);
    return () => mql.removeEventListener("change", listener);
  }, [preference]);

  // Cycle through the three available modes -------------------------------
  const cycleMode = () => {
    setPreference((prev) => (prev === "auto" ? "light" : prev === "light" ? "dark" : "auto"));
  };

  // Pick an icon that visually explains the *next* state the user will get.
  const nextIcon = preference === "auto" ? Sun : preference === "light" ? Moon : Laptop;
  const Icon = nextIcon;

  return (
    <button
      onClick={cycleMode}
      aria-label="Toggle colour-scheme"
      className="inline-flex items-center justify-center rounded-md p-2 bg-white dark:bg-gray-900 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900"
    >
      <Icon className="h-5 w-5 text-gray-600 dark:text-gray-200" />
    </button>
  );
}
